import React, { useState } from 'react';
import { Wrench, Plus, Edit, Trash2, Download, MapPin, Car, Package, ChevronDown } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../api.js';
import { PageTemplate } from '../common/PageTemplate.jsx';
import ToolForm from './forms/ToolForm.jsx';
import ToolSetForm from './forms/ToolSetForm.jsx';
import ToolDetailModal from './modals/ToolDetailModal.jsx';
import ToolAssignmentModal from './modals/ToolAssignmentModal.jsx';

// Temporary imports from index.js
import { useOrganizations, useVehicles, useWorkingSites, arrayToCSV, downloadCSV } from '../../index.js';
import Pagination, { usePagination } from '../common/Pagination.jsx';

// Tab configuration for PageTemplate
// Fonction et non constante de module : les libellés doivent suivre la langue courante.
const buildToolsTabs = (t) => [
  { id: 'tools', label: t('tools.tabs.tools'), icon: Wrench },
  { id: 'sets', label: t('tools.tabs.sets'), icon: Package },
];

function ToolsView({ onMessage }) {
  const { t } = useTranslation();

  // Use React Query hooks for data fetching
  const queryClient = useQueryClient();

  const { data: tools = [], isLoading: toolsLoading, error: toolsError } = useQuery({
    queryKey: ['tools'],
    queryFn: async () => {
      const response = await api.tool.getAll();
      return response.data || [];
    },
  });

  const { data: toolSets = [], isLoading: toolSetsLoading, error: toolSetsError } = useQuery({
    queryKey: ['toolSets'],
    queryFn: async () => {
      const response = await api.toolSet.getAll();
      return response.data || [];
    },
  });

  const { data: organizations = [] } = useOrganizations();
  const { data: vehicles = [] } = useVehicles();
  const { data: sites = [] } = useWorkingSites();

  // UI state (not server data)
  const [viewMode, setViewMode] = useState('tools'); // 'tools' or 'sets'
  const [showForm, setShowForm] = useState(false);
  const [showSetForm, setShowSetForm] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [editingToolSet, setEditingToolSet] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [showAssignment, setShowAssignment] = useState(false);
  const [assigningTool, setAssigningTool] = useState(null);
  const [orgFilter, setOrgFilter] = useState(''); // '' = all orgs
  const [categoryFilter, setCategoryFilter] = useState(''); // '' = all categories
  const [statusFilter, setStatusFilter] = useState(''); // '' = all statuses

  // Handle loading state
  const loading = toolsLoading;

  // Handle errors
  if (toolsError) {
    onMessage && onMessage({
      title: t('tools.toast.error'),
      message: t('tools.messages.loadError'),
      type: 'error'
    });
  }

  const handleFormSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['tools'] });
    setEditingTool(null);
    setShowForm(false);
  };

  const handleAssignmentSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['tools'] });
    setAssigningTool(null);
    setShowAssignment(false);
  };

  const handleEdit = (tool) => {
    setEditingTool(tool);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingTool(null);
  };

  const handleViewDetails = (tool) => {
    setSelectedTool(tool);
    setShowDetailView(true);
  };

  const handleCloseDetailView = () => {
    setShowDetailView(false);
    setSelectedTool(null);
  };

  const handleDetailViewUpdate = () => {
    queryClient.invalidateQueries({ queryKey: ['tools'] });
  };

  const handleAssign = (tool) => {
    setAssigningTool(tool);
    setShowAssignment(true);
  };

  const handleCloseAssignment = () => {
    setShowAssignment(false);
    setAssigningTool(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('tools.confirm.deleteTool'))) return;

    try {
      await api.tool.delete(id);
      onMessage && onMessage({
        title: t('tools.toast.success'),
        message: t('tools.messages.toolDeleted'),
        type: 'success'
      });
      queryClient.invalidateQueries({ queryKey: ['tools'] });
    } catch (error) {
      console.error('Error deleting tool:', error);
      onMessage && onMessage({
        title: t('tools.toast.error'),
        message: t('tools.messages.toolDeleteError'),
        type: 'error'
      });
    }
  };

  // Tool Set Handlers
  const handleSetFormSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['toolSets'] });
    setEditingToolSet(null);
    setShowSetForm(false);
  };

  const handleEditSet = (toolSet) => {
    setEditingToolSet(toolSet);
    setShowSetForm(true);
  };

  const handleCloseSetForm = () => {
    setShowSetForm(false);
    setEditingToolSet(null);
  };

  const handleDeleteSet = async (id) => {
    if (!window.confirm(t('tools.confirm.deleteSet'))) return;

    try {
      await api.toolSet.delete(id);
      onMessage && onMessage({
        title: t('tools.toast.success'),
        message: t('tools.messages.setDeleted'),
        type: 'success'
      });
      queryClient.invalidateQueries({ queryKey: ['toolSets'] });
    } catch (error) {
      console.error('Error deleting tool set:', error);
      onMessage && onMessage({
        title: t('tools.toast.error'),
        message: t('tools.messages.setDeleteError'),
        type: 'error'
      });
    }
  };

  const handleExportCSV = () => {
    const columns = [
      { key: 'id', label: t('tools.csv.id') },
      { key: 'tool_code', label: t('tools.csv.code') },
      { key: 'name', label: t('tools.csv.name') },
      { key: 'category', label: t('tools.csv.category') },
      { key: 'manufacturer', label: t('tools.csv.manufacturer') },
      { key: 'model', label: t('tools.csv.model') },
      { key: 'serial_number', label: t('tools.csv.serialNumber') },
      { key: 'condition', label: t('tools.csv.condition') },
      { key: 'status', label: t('tools.csv.status') },
      { key: 'assigned_to_type', label: t('tools.csv.assignedTo') },
      { key: 'purchase_date', label: t('tools.csv.purchaseDate') },
      { key: 'purchase_price', label: t('tools.csv.purchasePrice') },
    ];

    const csvContent = arrayToCSV(filteredTools, columns);
    const filename = `tools_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);

    onMessage && onMessage({
      title: t('tools.toast.success'),
      message: t('tools.messages.csvDone', { count: filteredTools.length }),
      type: 'success'
    });
  };

  // Helper function to get assignment info
  const getAssignmentInfo = (tool) => {
    if (tool.assigned_to_type === 'vehicle' && tool.assigned_vehicle_id) {
      const vehicle = vehicles.find(v => v.id === tool.assigned_vehicle_id);
      return vehicle ? `${vehicle.make} ${vehicle.model} (${vehicle.license_plate})` : t('tools.assignment.unknownVehicle');
    } else if (tool.assigned_to_type === 'location' && tool.assigned_location_id) {
      const site = sites.find(s => s.id === tool.assigned_location_id);
      return site ? site.site_name : t('tools.assignment.unknownSite');
    }
    return t('tools.assignment.unassigned');
  };

  // Helper function to format category
  const formatCategory = (category) => (
    category ? t(`tools.category.${category}`, { defaultValue: category }) : ''
  );

  // Helper function to format condition
  const formatCondition = (condition) => (
    condition ? t(`tools.condition.${condition}`, { defaultValue: condition }) : ''
  );

  // Helper function to format status
  const formatStatus = (status) => (
    status ? t(`tools.status.${status}`, { defaultValue: status }) : ''
  );

  // Filter tools
  const filteredTools = tools.filter(tool => {
    if (orgFilter && tool.org_id !== orgFilter) return false;
    if (categoryFilter && tool.category !== categoryFilter) return false;
    if (statusFilter && tool.status !== statusFilter) return false;
    return true;
  });

  // Pagination
  const { currentPage, setCurrentPage, itemsPerPage, setItemsPerPage, paginateArray } = usePagination(filteredTools.length, 1, 25);

  // Filter components for PageTemplate (tools tab)
  const toolsFilterComponents = (
    <>
      {/* Organization Filter */}
      <div className="relative">
        <select
          value={orgFilter}
          onChange={(e) => setOrgFilter(e.target.value)}
          className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
        >
          <option value="">{t('tools.filters.allOrgs')}</option>
          {organizations.map(org => (
            <option key={org.id} value={org.code}>
              {org.name} ({org.code})
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>

      {/* Category Filter */}
      <div className="relative">
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
        >
          <option value="">{t('tools.filters.allCategories')}</option>
          <option value="power_tool">{t('tools.category.power_tool')}</option>
          <option value="hand_tool">{t('tools.category.hand_tool')}</option>
          <option value="measuring_equipment">{t('tools.category.measuring_equipment')}</option>
          <option value="safety_equipment">{t('tools.category.safety_equipment')}</option>
          <option value="diagnostic_equipment">{t('tools.category.diagnostic_equipment')}</option>
          <option value="lifting_equipment">{t('tools.category.lifting_equipment')}</option>
          <option value="cutting_tool">{t('tools.category.cutting_tool')}</option>
          <option value="welding_equipment">{t('tools.category.welding_equipment')}</option>
          <option value="other">{t('tools.category.other')}</option>
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>

      {/* Status Filter */}
      <div className="relative">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
        >
          <option value="">{t('tools.filters.allStatuses')}</option>
          <option value="active">{t('tools.status.active')}</option>
          <option value="in_maintenance">{t('tools.status.in_maintenance')}</option>
          <option value="out_of_service">{t('tools.status.out_of_service')}</option>
          <option value="retired">{t('tools.status.retired')}</option>
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </>
  );

  // Action buttons
  const actionButtons = (
    <div className="flex gap-2">
      {viewMode === 'tools' && (
        <>
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 shadow-md"
            disabled={filteredTools.length === 0}
          >
            <Download className="w-5 h-5" />
            {t('tools.actions.exportCsv')}
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2 shadow-md"
          >
            <Plus className="w-5 h-5" />
            {t('tools.actions.newTool')}
          </button>
        </>
      )}
      {viewMode === 'sets' && (
        <button
          onClick={() => setShowSetForm(true)}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2 shadow-md"
        >
          <Plus className="w-5 h-5" />
          {t('tools.actions.newSet')}
        </button>
      )}
    </div>
  );

  return (
    <PageTemplate
      icon={Wrench}
      iconColor="text-amber-600"
      title={t('tools.page.title')}
      subtitle={t('tools.page.subtitle')}
      accentColor="amber"
      loading={loading}
      tabs={buildToolsTabs(t)}
      activeTab={viewMode}
      onTabChange={setViewMode}
      filters={viewMode === 'tools' ? toolsFilterComponents : null}
      actions={actionButtons}
    >
      {/* Modals */}
      <ToolForm
        isOpen={showForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        onMessage={onMessage}
        editingTool={editingTool}
      />

      <ToolSetForm
        isOpen={showSetForm}
        onClose={handleCloseSetForm}
        onSuccess={handleSetFormSuccess}
        onMessage={onMessage}
        editingToolSet={editingToolSet}
      />

      {showDetailView && (
        <ToolDetailModal
          tool={selectedTool}
          onClose={handleCloseDetailView}
          onMessage={onMessage}
          onUpdate={handleDetailViewUpdate}
        />
      )}

      <ToolAssignmentModal
        isOpen={showAssignment}
        onClose={handleCloseAssignment}
        onSuccess={handleAssignmentSuccess}
        onMessage={onMessage}
        tool={assigningTool}
        vehicles={vehicles}
        sites={sites}
      />

      {/* Tools View */}
      {viewMode === 'tools' && (
        <>

          {filteredTools.length === 0 ? (
            <div className="bg-white p-12 rounded-lg shadow-md text-center">
              <Wrench className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {orgFilter || categoryFilter || statusFilter ? t('tools.list.emptyFoundTitle') : t('tools.list.emptyTitle')}
              </h3>
              <p className="text-gray-600 mb-4">
                {orgFilter || categoryFilter || statusFilter
                  ? t('tools.list.emptyFilteredHint')
                  : t('tools.list.emptyHint')}
              </p>
              {!orgFilter && !categoryFilter && !statusFilter && (
                <button
                  onClick={() => setShowForm(true)}
                  className="px-6 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                >
                  {t('tools.actions.addTool')}
                </button>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('tools.list.columns.code')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('tools.list.columns.name')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('tools.list.columns.category')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('tools.list.columns.manufacturerModel')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('tools.list.columns.condition')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('tools.list.columns.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('tools.list.columns.assignment')}</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('tools.list.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginateArray(filteredTools).map(tool => (
                    <tr
                      key={tool.id}
                      onClick={() => handleViewDetails(tool)}
                      className="hover:bg-amber-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Wrench className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-900">{tool.tool_code}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{tool.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatCategory(tool.category)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{tool.manufacturer || '-'}</div>
                        <div className="text-sm text-gray-500">{tool.model || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          tool.condition === 'new' || tool.condition === 'excellent' ? 'bg-green-100 text-green-800' :
                          tool.condition === 'good' ? 'bg-blue-100 text-blue-800' :
                          tool.condition === 'fair' ? 'bg-yellow-100 text-yellow-800' :
                          tool.condition === 'poor' || tool.condition === 'broken' ? 'bg-red-100 text-red-800' :
                          tool.condition === 'lost' ? 'bg-gray-100 text-gray-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {formatCondition(tool.condition)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          tool.status === 'active' ? 'bg-green-100 text-green-800' :
                          tool.status === 'in_maintenance' ? 'bg-amber-100 text-amber-800' :
                          tool.status === 'out_of_service' ? 'bg-red-100 text-red-800' :
                          tool.status === 'retired' ? 'bg-gray-100 text-gray-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {formatStatus(tool.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {tool.assigned_to_type === 'vehicle' && <Car className="w-4 h-4 text-blue-500" />}
                          {tool.assigned_to_type === 'location' && <MapPin className="w-4 h-4 text-green-500" />}
                          <span className="text-sm text-gray-600">{getAssignmentInfo(tool)}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleAssign(tool); }}
                            className="text-green-600 hover:text-green-900"
                            title={t('tools.actions.assign')}
                          >
                            <MapPin className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(tool); }}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                          title={t('tools.actions.editTool')}
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(tool.id); }}
                          className="text-red-600 hover:text-red-900"
                          title={t('tools.actions.deleteTool')}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                currentPage={currentPage}
                totalItems={filteredTools.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
                className="px-4 py-3 border-t bg-gray-50"
              />
            </div>
          )}
        </>
      )}

      {/* Tool Sets View */}
      {viewMode === 'sets' && (
        <>
          {toolSets.length === 0 ? (
            <div className="bg-white p-12 rounded-lg shadow-md text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {t('tools.sets.emptyTitle')}
              </h3>
              <p className="text-gray-600 mb-4">
                {t('tools.sets.emptyHint')}
              </p>
              <button
                onClick={() => setShowSetForm(true)}
                className="px-6 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
              >
                {t('tools.actions.createSet')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {toolSets.map(toolSet => (
                <div
                  key={toolSet.id}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-amber-100 p-3 rounded-lg">
                        <Package className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{toolSet.name}</h3>
                        <p className="text-sm text-gray-500">{toolSet.org_id}</p>
                      </div>
                    </div>
                  </div>

                  {toolSet.description && (
                    <p className="text-sm text-gray-600 mb-4">{toolSet.description}</p>
                  )}

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      {toolSet.assigned_to_type === 'vehicle' && (
                        <>
                          <Car className="w-4 h-4 text-blue-500" />
                          <span className="text-gray-600">
                            {(() => {
                              const vehicle = vehicles.find(v => v.id === toolSet.assigned_vehicle_id);
                              return vehicle ? `${vehicle.make} ${vehicle.model} (${vehicle.license_plate})` : t('tools.assignment.unknownVehicle');
                            })()}
                          </span>
                        </>
                      )}
                      {toolSet.assigned_to_type === 'location' && (
                        <>
                          <MapPin className="w-4 h-4 text-green-500" />
                          <span className="text-gray-600">
                            {(() => {
                              const site = sites.find(s => s.id === toolSet.assigned_location_id);
                              return site ? site.site_name : t('tools.assignment.unknownSite');
                            })()}
                          </span>
                        </>
                      )}
                      {toolSet.assigned_to_type === 'unassigned' && (
                        <span className="text-gray-500">{t('tools.assignment.unassigned')}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditSet(toolSet)}
                        className="text-blue-600 hover:text-blue-900"
                        title={t('common.actions.edit')}
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteSet(toolSet.id)}
                        className="text-red-600 hover:text-red-900"
                        title={t('common.actions.delete')}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </PageTemplate>
  );
}

export default ToolsView;
